import { useContext, useEffect, useState } from 'react';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import { css } from '@emotion/react';
import prodPathReplace from 'shared/prod-path-rewrite';

import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { notifyFirstConnectionAnnounced } from 'redux/actions/mentoring-programs';

import NvModal, { BackdropType, ModalType } from 'shared/components/nv-modal';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import MentoringProgramContext from '../../context';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import { getRedirectToNewConnectionOnApproval } from 'redux/selectors/mentorship-program-connections';
import { redirectToNewConnectionOnApproval } from 'redux/actions/mentorship-program-connections';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';

const ANIMATION_DURATION = 3500;
const MODAL_WIDTH = 440;
const MODAL_HEIGHT = 487;
const IMAGE_WIDTH = 220;
const IMAGE_HEIGHT = 190;

const dateHasPassed = (date) => date && new Date(date) < new Date();

const styles = css`
  padding-bottom: 70px;
  .celebration-image {
    img {
      width: ${IMAGE_WIDTH}px;
      height: ${IMAGE_HEIGHT}px;
    }
  }
`;

const MatchCelebration = () => {
  const [announced, setAnnounced] = useState(false);
  const { injectServices } = useContext(AngularContext);
  const [$state, ConfettiAnimation] = injectServices(['$state', 'ConfettiAnimation']);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { isMentor } = useMentoringProgramRole();
  const { id, matchAnnouncementDate, mentorshipProgramEnrollment, programAlias, matchingType } = mentoringProgram;
  const isAutomatedMatching = matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING;
  const isParticipantDrivenMatching = matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN;
  const mentoringProgramEnrollment: MentoringProgramEnrollment = useSelector(state => (
    state.models.mentoringProgramEnrollments[mentorshipProgramEnrollment]
  ));
  const aliases = isMentor ? programAlias.mentee : programAlias.mentor;
  const { firstConnectionAnnounced, assignedConnections } = mentoringProgramEnrollment;
  const hasMultipleMentees = assignedConnections?.length > 1;
  const translationBase =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MATCH_CELEBRATION;
  const lastConnection = Array.isArray(assignedConnections) && assignedConnections.length > 0
    ? assignedConnections[assignedConnections.length - 1]
    : undefined;
  const lastConnectionName = lastConnection?.assignedEnrollment?.user?.firstName;
  const [participantDrivenText, setParticipantDrivenText] = useState(translationBase.PARTICIPANT_DRIVEN.TEXT(lastConnectionName));
  const autoMatchingText = hasMultipleMentees
    ? translationBase.AUTO_MATCHING.MULTIPLE_MENTEES(assignedConnections.length)
    : translationBase.AUTO_MATCHING.SINGLE_MENTEE(
        aliases.downcasedSingularized
      );
  const contentText = isParticipantDrivenMatching
    ? participantDrivenText
    : autoMatchingText;
  const connectionEnrollmentId = useSelector(getRedirectToNewConnectionOnApproval);

  const dispatch = useAppDispatch();

  const [showModal, setShowModal] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);

  const notifyCelebrationModalClosed = () => {
    if (isNotifying) return;
    setIsNotifying(true);
    wrapThunkAction(
      dispatch(notifyFirstConnectionAnnounced({
        programId: id,
        programEnrollmentId: mentorshipProgramEnrollment,
      })),
    ).then(() => {
      setIsNotifying(false);
      setAnnounced(true);
    });
  };

  const handleClose = () => {
    setShowModal(false);
    notifyCelebrationModalClosed();
  };

  const showCelebration = () => {
    ConfettiAnimation?.showConfetti();
    setShowModal(true);
    setTimeout(handleClose, ANIMATION_DURATION);
  };

  useEffect(() => {
    const shouldShowModal = !announced
      && (dateHasPassed(matchAnnouncementDate) || isParticipantDrivenMatching)
      && !firstConnectionAnnounced
      && assignedConnections?.length > 0;
    if (shouldShowModal) {
      showCelebration();
    }
  }, [matchAnnouncementDate, firstConnectionAnnounced]);

  useEffect(() => {
    if (connectionEnrollmentId && assignedConnections?.length > 0) {
      const connection = assignedConnections.find(({ assignedEnrollment }) => assignedEnrollment.id === connectionEnrollmentId);
      if (connection) {
        const { connectionId, assignedEnrollment: { user: { firstName } } } = connection;
        setParticipantDrivenText(translationBase.PARTICIPANT_DRIVEN.TEXT(firstName));
        showCelebration();
        dispatch(redirectToNewConnectionOnApproval({ enrollmentId: null }));
        setTimeout(() => {
          $state.go(
            'mentoring-program-single-connection',
            { connectionId },
            { notify: false }
          );
        }, 300);
      }
    }
  }, [connectionEnrollmentId]);

  return (
    <NvModal
      type={ModalType.FIXED}
      header=''
      headerBgColor='transparent'
      height={MODAL_HEIGHT}
      width={MODAL_WIDTH}
      show={showModal}
      onClose={handleClose}
      backdropType={BackdropType.LIGHTER}
      body={(
        <div
          css={styles}
          className='d-flex flex-column align-items-center justify-content-center h-100'
        >
          <div className='heading-3 mb-2'>{t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MATCH_CELEBRATION.TITLE()}</div>
          <div className='mb-2 text-center'>{contentText}</div>
          <div className='celebration-image'>
            <img
              src={prodPathReplace('images/match-celebration.gif')}
              alt={t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MATCH_CELEBRATION.ALT_IMAGE()}
            />
          </div>
        </div>
        )}
    />
  );
};

export default MatchCelebration;
